import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Button from 'ui/@_components/button-v2';
import BgGradientLeftIcon from 'ui/@_components/page-template/assets/bg-gradient-left.svg?react';
import BgGradientRightIcon from 'ui/@_components/page-template/assets/bg-gradient-right.svg?react';
import HorizontalProgressBar from 'ui/@_components/progress-bar/horizontal';
import styles from './styles.module.scss';

const SLOW_AFTER_MS = 10_000;
const TICK_MS = 250;
const RESUME_WITHIN_MS = 1_000;
const MAX_INDETERMINATE_PERCENTAGE = 90;

const ORB_ANIMATION = '/static/assets/images/app-start-orb.webp';
const ORB_STILL = '/static/assets/images/app-start-orb-still.webp';

const COPY = {
  title: 'Loading your culture dashboard',
  slow: 'Taking longer than usual. Check your connection, or reload the page.',
  reload: 'Reload',
};

let startedAt: number | null = null;
let hiddenAt: number | null = null;

/**
 * App start renders this loader several times in a row (the Suspense root, the
 * auth bootstrap, the onboarding guard), so the clock carries over between
 * mounts that follow each other closely instead of restarting at every step.
 */
const resolveStartedAt = () => {
  const now = performance.now();
  const resumes =
    startedAt !== null &&
    hiddenAt !== null &&
    now - hiddenAt < RESUME_WITHIN_MS;

  if (!resumes) {
    startedAt = now;
  }

  hiddenAt = null;

  return startedAt as number;
};

const useElapsedSinceAppStart = () => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const started = resolveStartedAt();
    setElapsed(performance.now() - started);

    const interval = window.setInterval(() => {
      setElapsed(performance.now() - started);
    }, TICK_MS);

    return () => {
      window.clearInterval(interval);
      hiddenAt = performance.now();
    };
  }, []);

  return elapsed;
};

const AppStartLoader = () => {
  const {t, ready} = useTranslation('components', {
    keyPrefix: 'app-start-loader',
    useSuspense: false,
  });

  const elapsed = useElapsedSinceAppStart();
  const isSlow = elapsed >= SLOW_AFTER_MS;

  const percentage = Math.min(
    MAX_INDETERMINATE_PERCENTAGE,
    (elapsed / SLOW_AFTER_MS) * MAX_INDETERMINATE_PERCENTAGE
  );

  const copy = (key: keyof typeof COPY) => (ready ? t(key) : COPY[key]);

  return (
    <div className={styles.container} role="status">
      <BgGradientLeftIcon className={styles.gradientLeft} />

      <BgGradientRightIcon className={styles.gradientRight} />

      <div className={styles.content}>
        <picture>
          <source media="(prefers-reduced-motion: reduce)" srcSet={ORB_STILL} />

          <img
            className={styles.orb}
            src={ORB_ANIMATION}
            alt=""
            width="72"
            height="72"
          />
        </picture>

        <HorizontalProgressBar
          size="small"
          percentage={percentage}
          wrapperBackgroundColor="rgba(255, 255, 255, 0.1)"
          progressBackgroundColor="linear-gradient(90deg, #3ad0f8 2.92%, #2961f6 89.2%)"
          containerClassName={styles.progress}
          progressBarClassName={styles.progressBar}
        />

        <p className={styles.message}>{copy(isSlow ? 'slow' : 'title')}</p>

        {isSlow && (
          <Button variant="secondary" onClick={() => window.location.reload()}>
            {copy('reload')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AppStartLoader;
